import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, MailIcon, PhoneIcon, SendIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { escaContacts, formatPhone } from '../../data/escaContacts';
import { track } from '../../utils/analytics';
const ENDPOINT = 'https://formspree.io/f/moeabdwj';
interface Fields {
  name: string;
  company: string;
  contact: string;
  role: string;
}
type Errors = Partial<Record<keyof Fields, string>>;
const EMPTY: Fields = {
  name: '',
  company: '',
  contact: '',
  role: ''
};
function getUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}
function isValidContact(value: string): boolean {
  const v = value.trim();
  if (v.includes('@')) return /^\S+@\S+\.\S{2,}$/.test(v);
  return v.replace(/\D/g, '').length >= 9;
}
export function ClassicDiscuss() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [values, setValues] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  const change = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track('form_start', {
        variant: 'classic'
      });
    }
    setValues(p => ({
      ...p,
      [key]: e.target.value
    }));
    setErrors(p => p[key] ? {
      ...p,
      [key]: undefined
    } : p);
  };
  const validate = (): boolean => {
    const next: Errors = {};
    if (!values.name.trim()) next.name = c.contact.errors.name;
    if (!values.contact.trim()) next.contact = c.contact.errors.contact;else if (!isValidContact(values.contact)) next.contact = c.contact.errors.contactInvalid;
    if (!values.role.trim()) next.role = c.contact.errors.role;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFailed(false);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: values.name,
          company: values.company,
          contact: values.contact,
          looking_for: values.role,
          source: 'esca_classic_discuss',
          ...getUtm()
        })
      });
      if (!res.ok) throw new Error('failed');
      track('form_submit', {
        source: 'esca_classic_discuss'
      });
      setDone(true);
      setValues(EMPTY);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };
  const inputCls = (invalid: boolean) => `w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${invalid ? 'border-red-400' : 'border-navy-200'}`;
  return <section id="contact" className="relative py-20 lg:py-28 bg-navy-50/50 overflow-hidden" ref={ref} data-id="element-553" data-mp="2ajo47qcl0kp7">
      <div className="absolute inset-0 pointer-events-none" data-id="element-554" data-mp="144et1m3yol49">
        <div className="absolute top-0 right-0 w-[450px] h-[450px] rounded-full bg-primary-100/25 blur-3xl hidden sm:block" data-id="element-555" data-mp="z48zg0e4sojj" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-cyan-100/20 blur-3xl hidden sm:block" data-id="element-556" data-mp="2ny6hrvc2k6o9" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-557" data-mp="2sqhi2zaq1uhc">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-558" data-mp="2e1bl7hzcwgcm">
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.55
        }} data-id="element-559" data-mp="2iz3plbh6tuxp">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-560" data-mp="36n3fk96odhb8">
              {lang === 'EN' ? 'Get in touch' : "Зв'яжіться з нами"}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-561" data-mp="2wqi5h9fstg0j">
              {c.contact.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-562" data-mp="1lbbmy1zd21iq">
              {c.contact.lead}
            </p>

            <div className="mt-9 space-y-3" data-id="element-563" data-mp="1qbhdtfxt3jzr">
              {escaContacts.phones.map(p => <a key={p} href={`tel:${p}`} onClick={() => track('phone_click', {
              phone: p
            })} className="flex items-center gap-3.5 text-navy-700 hover:text-primary-600 transition-colors" data-id="element-564" data-mp="8brnq77pq2mo">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-navy-200 text-primary-600 shadow-sm" data-id="element-565" data-mp="dbw8wajkflj8">
                    <PhoneIcon className="w-4 h-4" data-id="element-566" data-mp="3lpl917yta5j4" />
                  </span>
                  <span className="font-semibold tabular-nums" data-id="element-567" data-mp="3guphe8xhlk6j">
                    {formatPhone(p)}
                  </span>
                </a>)}
              <a href={`mailto:${escaContacts.email}`} onClick={() => track('email_click')} className="flex items-center gap-3.5 text-navy-700 hover:text-primary-600 transition-colors" data-id="element-568" data-mp="3ptav9qs0kb0z">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-navy-200 text-primary-600 shadow-sm" data-id="element-569" data-mp="2y0aiejb6drip">
                  <MailIcon className="w-4 h-4" data-id="element-570" data-mp="15zse4r9sk7zj" />
                </span>
                <span className="font-semibold" data-id="element-571" data-mp="1artyz1l0z716">{escaContacts.email}</span>
              </a>
              <a href={escaContacts.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3.5 text-navy-700 hover:text-primary-600 transition-colors" data-id="element-572" data-mp="1fqdkj0vpigt0">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-navy-200 text-primary-600 shadow-sm" data-id="element-573" data-mp="n2806f8kettp">
                  <SendIcon className="w-4 h-4" data-id="element-574" data-mp="x0fdmo3natqy" />
                </span>
                <span className="font-semibold" data-id="element-575" data-mp="23g7xdig105d9">Telegram</span>
              </a>
            </div>

            <div className="mt-9 flex items-start gap-3 p-4 rounded-xl bg-white border border-navy-200 shadow-sm" data-id="element-576" data-mp="28far46qk2p5x">
              <ShieldCheckIcon className="flex-shrink-0 w-5 h-5 mt-0.5 text-emerald-500" data-id="element-577" data-mp="3sx3d90hkdwyn" />
              <p className="text-sm text-navy-600 leading-relaxed" data-id="element-578" data-mp="3nyie74e1tjbs">
                {c.contact.disclaimer}
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 24
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.55,
          delay: 0.1
        }} data-id="element-579" data-mp="3j5pckpdvmr3x">
            {done ? <div className="p-8 lg:p-10 rounded-2xl bg-white border border-navy-200 shadow-xl shadow-navy-200/30 text-center" data-id="element-580" data-mp="397168nv980g2">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-500" data-id="element-581" data-mp="3085y368ngga4">
                  <CheckCircleIcon className="w-8 h-8" data-id="element-582" data-mp="2vaw9ttwzyanv" />
                </div>
                <h3 className="mt-6 text-2xl font-bold text-navy-900" data-id="element-583" data-mp="yhkt247basa5">
                  {c.contact.successTitle}
                </h3>
                <p className="mt-3 text-navy-500 leading-relaxed" data-id="element-584" data-mp="tj137splkwci">
                  {c.contact.successText}
                </p>
                <button type="button" onClick={() => {
              setDone(false);
              startedRef.current = false;
            }} className="mt-7 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-585" data-mp="7byx6cx4li7e">
                  {c.contact.successReset}
                </button>
              </div> : <form onSubmit={submit} noValidate className="p-6 sm:p-8 lg:p-10 rounded-2xl bg-white border border-navy-200 shadow-xl shadow-navy-200/30" data-id="element-586" data-mp="3kr4yl6yrpqoe">
                <div className="space-y-5" data-id="element-587" data-mp="3atgrrinw0spv">
                  <div data-id="element-588" data-mp="3auidqi6fyzmd">
                    <label htmlFor="ec-name" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-589" data-mp="141pt8gl2fdfb">
                      {c.contact.nameLabel}
                    </label>
                    <input id="ec-name" type="text" autoComplete="name" value={values.name} onChange={change('name')} placeholder={c.contact.namePlaceholder} className={inputCls(!!errors.name)} data-id="element-590" data-mp="2tjmmwav085m4" />
                    {errors.name && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-591" data-mp="3cbxl3reprfdw">
                        {errors.name}
                      </p>}
                  </div>

                  <div data-id="element-592" data-mp="3m7ijlfkkpxiw">
                    <label htmlFor="ec-company" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-593" data-mp="3m5o5ol7pz5ob">
                      {c.contact.companyLabel}
                    </label>
                    <input id="ec-company" type="text" autoComplete="organization" value={values.company} onChange={change('company')} placeholder={c.contact.companyPlaceholder} className={inputCls(false)} data-id="element-594" data-mp="1mvy5brglarpa" />
                  </div>

                  <div data-id="element-595" data-mp="1uhmhpq7q0cl0">
                    <label htmlFor="ec-contact" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-596" data-mp="1ui4ecnyaostg">
                      {c.contact.contactLabel}
                    </label>
                    <input id="ec-contact" type="text" value={values.contact} onChange={change('contact')} placeholder={c.contact.contactPlaceholder} className={inputCls(!!errors.contact)} data-id="element-597" data-mp="2hfz6gzxgw3p8" />
                    {errors.contact && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-598" data-mp="rnyur9gq0xkc">
                        {errors.contact}
                      </p>}
                  </div>

                  <div data-id="element-599" data-mp="10odkqm4mr0u6">
                    <label htmlFor="ec-role" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-600" data-mp="10o40zga6hpwx">
                      {c.contact.roleLabel}
                    </label>
                    <textarea id="ec-role" rows={3} value={values.role} onChange={change('role')} placeholder={c.contact.rolePlaceholder} className={`${inputCls(!!errors.role)} resize-none`} data-id="element-601" data-mp="1amb7f56ucpko" />
                    {errors.role && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-602" data-mp="1tem58kmn1xbx">
                        {errors.role}
                      </p>}
                  </div>
                </div>

                {failed && <p className="mt-5 text-sm text-red-500 font-medium" data-id="element-603" data-mp="3pg7eg44j7shj">
                    {c.contact.errorGeneric}
                  </p>}

                <button type="submit" disabled={submitting} className="group mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-colors shadow-lg shadow-primary-600/25 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-604" data-mp="81ac4djnfnia">
                  {submitting ? <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-605" data-mp="lxe2fvrejbph" />
                      {c.contact.submitting}
                    </> : <>
                      {c.contact.submit}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-606" data-mp="13a43onghiffk" />
                    </>}
                </button>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}